import React, { useEffect, useState } from 'react'

import SensorInsights, { StatusBadge, formatPrice } from './adasInsights'

// Read-only Mechanical section for the client portal. Mirrors AdasSections but
// with no edit/remove affordances. Items are grouped by subcategory (panel.name)
// and each subcategory reuses the shared SensorInsights block — matched to the
// report's existing ADAS insight data by rawSensorId — exactly like the ADAS
// "Diagnostic Scans" category.

// Record separator, used to join the subcategory names into one stable primitive
// for the expansion effects. A plain '|' would be ambiguous: a subcategory name
// is free text stamped from the mechanical catalog.
const GROUP_LIST_SEP = '\u001E'

const SCAN_BASE_IDS = [
  'pre-repair-scan',
  'in-process-scan',
  'post-repair-scan',
  'pre-and-post-repair-scan',
]

const MechanicalSection = ({ report, allowViewPricing, expandAll, expandNonce }) => {
  // Which subcategories are expanded, keyed by group name. Held here rather than
  // inside SensorInsights so the subcategory header — a sibling of it — can pin
  // only while its own section is open. Same shape as AdasSections.
  const [openByGroup, setOpenByGroup] = useState({})
  const mechanical = report && report.mechanical
  const panels = (mechanical && mechanical.panels) || []
  const adasServices = (report && report.adas && report.adas.services) || []

  const scanBaseId = (svc) => {
    const opId = svc && svc.operation && svc.operation.id
      ? String(svc.operation.id).toLowerCase()
      : ''
    if (!opId) return null
    return SCAN_BASE_IDS.find(base => opId === base || opId.indexOf(`${base}-`) === 0) || null
  }

  // Relink a seeded mechanical scan row to its ADAS "Diagnostic Scans" data so it
  // renders the SAME insights (QA #26). getSensorInsight/getLinkedSafetySystems
  // key on rawSensorId (adasThink UUID); getSensorDocuments keys on sensorId
  // (slug). A seeded row's own rawSensorId is the scan slug, matching neither —
  // so expose the API-stamped UUID as rawSensorId and carry the slug as sensorId.
  const enrichItem = (item) => {
    if (!item) return item
    let next
    if (item.sensorId || item.adasRawSensorId) {
      next = { ...item, sensorId: item.sensorId || null, rawSensorId: item.adasRawSensorId || item.rawSensorId }
    } else {
      const bySlug = adasServices.find(svc => scanBaseId(svc) && scanBaseId(svc) === item.rawSensorId)
      const src = bySlug || (item.sensorName && adasServices.find(svc => svc.sensorName === item.sensorName))
      next = src
        ? { ...item, sensorId: src.sensorId || item.sensorId, rawSensorId: src.rawSensorId || item.rawSensorId, sensorName: src.sensorName || item.sensorName }
        : item
    }
    // Inherit the linked ADAS service's collision flag so the mechanical
    // diagnostics insights render IDENTICALLY to the ADAS diagnostics insights
    // (the "…being in a collision." justification, not the conditions grid with
    // status icons). Seeded mechanical rows don't carry collisionRelated (QA #24).
    if (!next.collisionRelated) {
      const linked = adasServices.find(svc =>
        (next.rawSensorId && svc.rawSensorId === next.rawSensorId) ||
        (next.sensorId && svc.sensorId === next.sensorId) ||
        (next.sensorName && svc.sensorName === next.sensorName))
      if (linked && linked.collisionRelated) {
        next = { ...next, collisionRelated: true }
      }
    }
    return next
  }

  // Keep only active items per panel, preserving panel grouping/order.
  const groups = panels
    .map(panel => ({
      name: (panel && panel.name) || 'Mechanical',
      items: ((panel && panel.items) || []).filter(item => item && item.active).map(enrichItem),
    }))
    .filter(group => group.items.length > 0)

  const totalItems = groups.reduce((count, group) => count + group.items.length, 0)

  // Joined group names — a stable primitive for the effects below, which have to
  // run above the early return. U+001E (record separator) rather than '|': a
  // subcategory name is free text stamped from the mechanical catalog.
  const groupKey = groups.map(group => group.name).join(GROUP_LIST_SEP)

  // Expand by default once mechanical data lands, matching the ADAS sections
  // (#37249). Seeds only keys not already in the map: a group the user collapsed
  // keeps its state, and a group that arrives later — a different report on the
  // same mounted component — opens like its siblings instead of alone and
  // collapsed. Returning `prev` unchanged is what stops this re-running.
  useEffect(() => {
    if (!groupKey) return
    setOpenByGroup(prev => {
      const names = groupKey.split(GROUP_LIST_SEP)
      if (names.every(name => name in prev)) return prev
      const next = { ...prev }
      names.forEach(name => { if (!(name in next)) next[name] = true })
      return next
    })
  }, [groupKey])

  // Respond to the report header's Expand/Collapse All. This card used to ignore
  // it entirely: the buttons live in the ADAS card's header and the broadcast was
  // never passed down here, so mechanical insights stayed closed regardless.
  useEffect(() => {
    if (!groupKey) return
    const next = {}
    groupKey.split(GROUP_LIST_SEP).forEach(name => { next[name] = !!expandAll })
    setOpenByGroup(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAll, expandNonce])

  const toggleGroup = (name, next) =>
    setOpenByGroup(prev => ({ ...prev, [name]: next }))

  // Hide the whole section when there is no mechanical data / no active items.
  if (!mechanical || !totalItems) return null

  const rate = mechanical.laborRateMechanical || 0
  const sensorInsights = report.sensorInsights || []
  const collision = !!report.vehicleInvolvedInCollision

  const lineTotal = (item) => (item.hours || 0) * rate
  const mechanicalTotal = groups.reduce((sum, group) => (
    sum + group.items.reduce((groupSum, item) => groupSum + lineTotal(item), 0)
  ), 0)

  const formatHours = (hours) => (
    (hours || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  )

  return (
    <div className='adas-detail__report-card'>
      <div className='adas-detail__report-header'>
        <div className='adas-detail__report-header-left'>
          <h3 className='adas-detail__report-title'>Mechanical</h3>
        </div>
      </div>

      {/* Figma 19569:216578 — the section header IS the rate row: a subcategory
          bar carrying the "Mechanical" tag on the left and the rate on the
          right. Replaces the old full-width blue info bar, closing the gap this
          portal was left with when the admin portal took the same frame.
          Read-only here, so the rate field renders as text, not an input.

          The tag is .adas-detail__mechanical-subcategory-tag (19569:216579,
          Gray 400), NOT .adas-detail__group-category (18796:156118, Gray 500)
          used by the group headers below — two elements, two nodes, two greys.
          Do not collapse them. */}
      <div className='adas-detail__mechanical-subcategory-bar'>
        <span className='adas-detail__mechanical-subcategory-tag'>Mechanical</span>
        {allowViewPricing && (
          <div className='adas-detail__mechanical-rate'>
            <span className='adas-detail__mechanical-rate-label'>Rate</span>
            <div className='adas-detail__mechanical-rate-value'>
              <span className='adas-detail__mechanical-rate-symbol'>$</span>
              <span className='adas-detail__mechanical-rate-amount'>{rate.toFixed(2)}</span>
              <span className='adas-detail__mechanical-rate-unit'>/ hr</span>
            </div>
          </div>
        )}
      </div>

      <div>
        {groups.map((group, groupIdx) => (
          <div key={`${group.name}-${groupIdx}`} className='adas-detail__sensor-group'>
            <div
              className={`adas-detail__group-header${openByGroup[group.name] ? ' adas-detail__group-header--sticky' : ''}`}
            >
              <span className='adas-detail__group-category'>Mechanical</span>
              <span className='adas-detail__group-sensor'>{group.name}</span>
            </div>

            {group.items.map((item, itemIdx) => (
              <div key={item._id || `${group.name}-${itemIdx}`} className='adas-detail__service-row'>
                <div className='adas-detail__service-name'>
                  <span>{item.name || '—'}</span>
                </div>
                <div className='adas-detail__service-status'>
                  <span className='adas-detail__meta-label'>Status:</span>
                  <StatusBadge status={item.recommendedStatus} source={item.recommendedSource} />
                </div>
                <div className='adas-detail__mech-labor'>
                  <span className='adas-detail__meta-label'>Labor Hours:</span>
                  <span className='adas-detail__price-value'>{formatHours(item.hours)} hrs</span>
                  {allowViewPricing && (
                    <span className='adas-detail__price-value'>
                      &times; {formatPrice(rate)}/hr = {formatPrice(lineTotal(item))}
                    </span>
                  )}
                </div>
              </div>
            ))}

            <SensorInsights
              sensorName={group.name}
              services={group.items}
              sensorInsights={sensorInsights}
              report={report}
              collision={collision}
              open={!!openByGroup[group.name]}
              onToggle={next => toggleGroup(group.name, next)}
              includeCustom
            />
          </div>
        ))}

        {allowViewPricing && (
          <div className='adas-detail__service-row'>
            <div className='adas-detail__service-name'>
              <span className='adas-detail__price-value'>Mechanical Total</span>
            </div>
            <div className='adas-detail__service-status' />
            <div className='adas-detail__mech-labor'>
              <span className='adas-detail__price-value'>{formatPrice(mechanicalTotal)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MechanicalSection
